import React, { useMemo, useState } from "react";
import ImageDropzone from "@/components/ImageDropzone";
import BuyCreditsModal from "@/components/BuyCreditsModal";
import { VariationSetsAPI } from "@/lib/api";
import { useAuth } from "@/lib/providers";

// Default to the cheapest option to protect credit budget; switching to a heavier
// model triggers an explicit confirm popup.
const MODELS = [
  {
    provider: "gemini", model: "gemini-2.5-flash",
    label: "Gemini 2.5 Flash",
    tagline: "Fastest · lightest on credits · default",
    mins_per_var: 0.5, extra_per_extra_var: 0.2,
    credits_per_var: 0.005,
    recommended: true,
  },
  {
    provider: "gemini", model: "gemini-2.5-pro",
    label: "Gemini 2.5 Pro",
    tagline: "Solid all-round · ~25× more credits than Flash",
    mins_per_var: 1.0, extra_per_extra_var: 0.4,
    credits_per_var: 0.13,
  },
  {
    provider: "anthropic", model: "claude-haiku-4-5-20251001",
    label: "Claude Haiku 4.5",
    tagline: "Sharper visual reading · ~10× more credits than Flash",
    mins_per_var: 1.5, extra_per_extra_var: 0.5,
    credits_per_var: 0.05,
  },
  {
    provider: "anthropic", model: "claude-sonnet-4-5-20250929",
    label: "Claude Sonnet 4.5",
    tagline: "Most nuanced critique · ~50× more credits than Flash",
    mins_per_var: 3.0, extra_per_extra_var: 1.0,
    credits_per_var: 0.30,
  },
];

function estimateMinutes(model, nVars) {
  if (!model) return null;
  // Single-call engine: time scales with number of images, not variations × dims
  const extra = Math.max(0, nVars - 1) * model.extra_per_extra_var;
  const mins = model.mins_per_var + extra;
  return Math.max(0.3, Math.round(mins * 2) / 2);
}

function estimateCredits(model, nVars) {
  if (!model) return null;
  return Math.max(0.001, (model.credits_per_var || 0.005) * nVars);
}

function formatEstimate(mins) {
  if (mins == null) return "—";
  if (mins < 1) return `${Math.round(mins * 60)}s`;
  if (mins === Math.floor(mins)) return `~${mins} min`;
  return `~${mins.toFixed(1)} min`;
}

const newVariation = (idx) => ({
  key: `v${Date.now()}_${idx}`,
  name: `V${idx + 1}`,
  files: [],
});

export default function NewVariationSet({ project, onClose, onCreated }) {
  const { user } = useAuth();
  const [title, setTitle] = useState(`${project.brand_name} — variation comparison`);
  const [designerName, setDesignerName] = useState(user?.name || "");
  const [extra, setExtra] = useState("");
  const recommended = MODELS.find((m) => m.recommended) || MODELS[0];
  const [modelKey, setModelKey] = useState(`${recommended.provider}|${recommended.model}`);
  const [variations, setVariations] = useState([newVariation(0), newVariation(1)]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  // Safety-net paywall for stale balance (same pattern as NewAssessment).
  const [showPaywall, setShowPaywall] = useState(false);

  const selectedModel = useMemo(
    () => MODELS.find((m) => `${m.provider}|${m.model}` === modelKey),
    [modelKey],
  );
  const estimate = estimateMinutes(selectedModel, variations.length);
  const credits = estimateCredits(selectedModel, variations.length);
  const [pendingModelKey, setPendingModelKey] = useState(null);   // model awaiting warning confirm

  const tryPickModel = (m) => {
    const key = `${m.provider}|${m.model}`;
    if (key === modelKey) return;
    if (m.recommended) {
      setModelKey(key);
    } else {
      setPendingModelKey(key);
    }
  };
  const confirmPendingModel = () => {
    if (pendingModelKey) setModelKey(pendingModelKey);
    setPendingModelKey(null);
  };
  const pendingModel = pendingModelKey
    ? MODELS.find((m) => `${m.provider}|${m.model}` === pendingModelKey)
    : null;
  const defaultModel = MODELS.find((m) => m.recommended);

  const setVar = (i, patch) => setVariations((xs) => xs.map((v, idx) => (idx === i ? { ...v, ...patch } : v)));
  const addVar = () => setVariations((xs) => (xs.length >= 4 ? xs : [...xs, newVariation(xs.length)]));
  const removeVar = (i) => setVariations((xs) => (xs.length <= 2 ? xs : xs.filter((_, idx) => idx !== i)));

  const submit = async () => {
    setError("");
    if (variations.some((v) => !v.name.trim())) return setError("Each variation needs a name.");
    if (variations.some((v) => v.files.length === 0)) return setError("Upload at least one image per variation.");
    const [provider, model] = modelKey.split("|");
    setSubmitting(true);
    try {
      const { data } = await VariationSetsAPI.create(project.project_id, {
        project_id: project.project_id,
        title,
        designer_name: designerName,
        model_provider: provider,
        model_name: model,
        extra_context: extra,
        variations: variations.map((v) => ({
          name: v.name,
          pack_images_b64: v.files.map((f) => f.b64),
          pack_image_mimes: v.files.map((f) => f.mime),
        })),
      });
      onCreated(data.variation_set_id);
    } catch (e) {
      const detail = e?.response?.data?.detail || e.message;
      if (e?.response?.status === 402) {
        setShowPaywall(true);
        setError("");
      } else {
        setError(detail);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div role="dialog" data-testid="new-variation-set-modal" style={{
      position: "fixed", inset: 0, background: "color-mix(in srgb, var(--v-bg) 70%, transparent)",
      backdropFilter: "blur(8px)", zIndex: 80, overflow: "auto",
    }}>
      <div className="v-container" style={{ paddingTop: 48, paddingBottom: 80 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div className="overline">Compare design variations</div>
          <button className="v-btn v-btn-outline" data-testid="close-variation-modal" onClick={onClose}>Close ×</button>
        </div>
        <h1 className="display" style={{ fontSize: "clamp(32px,4vw,52px)", marginTop: 8, lineHeight: 1 }}>
          Compare 2–4 variations of the same design.
        </h1>
        <p style={{ color: "var(--v-text-2)", marginTop: 12, maxWidth: 640 }}>
          Each variation gets a full 7-dimension audit, plus a side-by-side comparison that picks the variation
          to ship.
        </p>

        <div className="v-surface" style={{ padding: 28, marginTop: 32 }}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Field label="Comparison title" value={title} onChange={setTitle} testId="vset-field-title" />
            <Field label="Designer name" value={designerName} onChange={setDesignerName} testId="vset-field-designer" />
          </div>

          <div style={{ marginTop: 24 }}>
            <div className="overline" style={{ marginBottom: 6 }}>AI critic — pick by time vs nuance</div>
            <div className="mono" style={{ color: "var(--v-text-2)", fontSize: 12, marginBottom: 10, lineHeight: 1.5 }}>
              All times below assume {variations.length} {variations.length === 1 ? "variation" : "variations"}. Add or remove variations to see the estimate update.
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="model-card-picker">
              {MODELS.map((m) => {
                const key = `${m.provider}|${m.model}`;
                const isSel = key === modelKey;
                const mins = estimateMinutes(m, variations.length);
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => tryPickModel(m)}
                    data-testid={`model-card-${m.model}`}
                    style={{
                      textAlign: "left",
                      padding: "16px 18px",
                      background: isSel ? "color-mix(in srgb, var(--v-accent) 8%, transparent)" : "var(--v-bg)",
                      border: isSel ? "1px solid var(--v-accent)" : "1px solid var(--v-border)",
                      cursor: "pointer",
                      borderRadius: 2,
                      position: "relative",
                    }}
                  >
                    {m.recommended && (
                      <div className="overline" style={{
                        position: "absolute", top: 10, right: 14, fontSize: 9, color: "var(--v-accent)",
                      }}>
                        Recommended
                      </div>
                    )}
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 12 }}>
                      <div style={{ fontSize: 15, fontWeight: 700, color: "var(--v-text)" }}>{m.label}</div>
                      <div style={{
                        fontSize: 16, fontWeight: 700,
                        color: isSel ? "var(--v-accent)" : "var(--v-text-2)",
                        whiteSpace: "nowrap",
                      }}>
                        {formatEstimate(mins)}
                      </div>
                    </div>
                    <div className="mono" style={{
                      color: "var(--v-text-2)", fontSize: 11, marginTop: 4,
                      letterSpacing: ".06em",
                    }}>
                      {m.tagline}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          <div style={{ marginTop: 16 }}>
            <div className="overline" style={{ marginBottom: 6 }}>Extra context (optional)</div>
            <textarea
              className="v-textarea" value={extra} onChange={(e) => setExtra(e.target.value)}
              placeholder="What is each variation trying to do differently?"
              data-testid="vset-field-extra"
            />
          </div>

          <div style={{ marginTop: 32 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
              <div className="overline">Variations · {variations.length} of max 4</div>
              <button
                className="v-btn v-btn-outline"
                onClick={addVar}
                disabled={variations.length >= 4}
                data-testid="vset-add-variation"
              >
                + Add variation
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {variations.map((v, i) => (
                <div key={v.key} className="v-surface" style={{ padding: 20, background: "var(--v-bg)" }} data-testid={`variation-card-${i}`}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                    <div className="overline">Variation {i + 1}</div>
                    {variations.length > 2 && (
                      <button
                        type="button"
                        onClick={() => removeVar(i)}
                        data-testid={`variation-remove-${i}`}
                        style={{ background: "transparent", border: "none", color: "var(--v-text-2)", cursor: "pointer", fontSize: 12 }}
                      >
                        Remove ×
                      </button>
                    )}
                  </div>
                  <input
                    className="v-input"
                    value={v.name}
                    onChange={(e) => setVar(i, { name: e.target.value })}
                    placeholder={`V${i + 1}`}
                    data-testid={`variation-name-${i}`}
                  />
                  <div style={{ marginTop: 14 }}>
                    <ImageDropzone
                      files={v.files}
                      setFiles={(fs) => setVar(i, { files: fs })}
                      label="Pack images"
                      testId={`variation-dropzone-${i}`}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {error && (
            <div className="mono" style={{ color: "var(--v-accent)", marginTop: 16 }} data-testid="vset-error">
              {error}
            </div>
          )}

          <div style={{
            marginTop: 32, padding: "20px 22px",
            background: "color-mix(in srgb, var(--v-accent) 6%, transparent)",
            borderLeft: "3px solid var(--v-accent)",
          }} data-testid="vset-estimate-card">
            <div className="overline">Estimated time + credit usage · {selectedModel?.label}</div>
            <div style={{ display: "flex", alignItems: "baseline", gap: 22, marginTop: 6, flexWrap: "wrap" }}>
              <div>
                <div style={{ fontSize: 32, fontWeight: 800, lineHeight: 1, color: "var(--v-accent)" }} data-testid="vset-estimate">
                  {formatEstimate(estimate)}
                </div>
                <div className="mono" style={{ color: "var(--v-text-2)", fontSize: 10, marginTop: 4, letterSpacing: ".1em" }}>WALL-CLOCK</div>
              </div>
              <div>
                <div style={{ fontSize: 32, fontWeight: 800, lineHeight: 1, color: "var(--v-accent)" }} data-testid="vset-credits-estimate">
                  {credits < 0.01 ? "<0.01" : credits.toFixed(2)} <span style={{ fontSize: 14, fontWeight: 500 }}>cr</span>
                </div>
                <div className="mono" style={{ color: "var(--v-text-2)", fontSize: 10, marginTop: 4, letterSpacing: ".1em" }}>CREDIT COST</div>
              </div>
              <div className="mono" style={{ color: "var(--v-text-2)", fontSize: 12, alignSelf: "flex-end" }}>
                for {variations.length} {variations.length === 1 ? "variation" : "variations"}
              </div>
            </div>
            <p style={{ marginTop: 10, color: "var(--v-text)", fontSize: 13, lineHeight: 1.55 }}>
              You can <b>leave this tab open and go do something else</b> — the audit keeps running on the server.
              Come back in <b>{formatEstimate(estimate)}</b> and the results will be waiting.
              <span style={{ color: "var(--v-text-2)" }}> Please don't close the window for the project page if you want to be notified inline.</span>
            </p>
          </div>

          <div style={{ marginTop: 24, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
            <div className="mono" style={{ color: "var(--v-text-2)", fontSize: 12 }}>
              Single-call comparison · all variations scored in one pass to save credits.
            </div>
            <button
              className="v-btn"
              data-testid="vset-submit"
              disabled={submitting}
              onClick={submit}
            >
              {submitting ? "Queueing…" : `Run comparison · ${formatEstimate(estimate)} →`}
            </button>
          </div>
        </div>
      </div>

      {pendingModel && defaultModel && (
        <HeavyModelWarning
          pending={pendingModel}
          defaultModel={defaultModel}
          variationCount={variations.length}
          onCancel={() => setPendingModelKey(null)}
          onConfirm={confirmPendingModel}
        />
      )}
      <BuyCreditsModal open={showPaywall} onClose={() => setShowPaywall(false)} creditsAvailable={0} />
    </div>
  );
}

function HeavyModelWarning({ pending, defaultModel, variationCount, onCancel, onConfirm }) {
  const ratio = Math.max(1, Math.round(pending.credits_per_var / (defaultModel.credits_per_var || 0.005)));
  const pendingCredits = (pending.credits_per_var * variationCount);
  const defaultCredits = (defaultModel.credits_per_var * variationCount);
  return (
    <div
      role="dialog"
      data-testid="heavy-model-warning"
      style={{
        position: "fixed", inset: 0, background: "color-mix(in srgb, var(--v-bg) 60%, rgba(0,0,0,.4))",
        backdropFilter: "blur(6px)", zIndex: 100,
        display: "flex", alignItems: "center", justifyContent: "center", padding: 24,
      }}
      onClick={onCancel}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="v-surface"
        style={{ maxWidth: 520, width: "100%", padding: 32, borderLeft: "3px solid #F59E0B" }}
      >
        <div className="overline" style={{ color: "#F59E0B" }}>Heads up · heavier credit usage</div>
        <h2 style={{ fontSize: 26, marginTop: 6, lineHeight: 1.15 }}>
          {pending.label} will consume ~{ratio}× more credits than {defaultModel.label}.
        </h2>
        <div style={{ marginTop: 18, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <div className="v-surface" style={{ padding: 14, background: "var(--v-bg)" }}>
            <div className="overline">{defaultModel.label} (default)</div>
            <div style={{ fontSize: 22, fontWeight: 800, marginTop: 4 }}>
              {defaultCredits < 0.01 ? "<0.01" : defaultCredits.toFixed(2)} cr
            </div>
          </div>
          <div className="v-surface" style={{ padding: 14, background: "var(--v-bg)", borderColor: "#F59E0B" }}>
            <div className="overline" style={{ color: "#F59E0B" }}>{pending.label}</div>
            <div style={{ fontSize: 22, fontWeight: 800, marginTop: 4, color: "#F59E0B" }}>
              {pendingCredits < 0.01 ? "<0.01" : pendingCredits.toFixed(2)} cr
            </div>
          </div>
        </div>
        <p style={{ marginTop: 18, color: "var(--v-text-2)", fontSize: 13, lineHeight: 1.6 }}>
          {pending.tagline}. Choose this model only if the nuance is worth the extra credit cost — it pulls
          from your <b>monthly credit allowance</b>.
        </p>
        <div style={{ display: "flex", gap: 12, marginTop: 22, justifyContent: "flex-end" }}>
          <button className="v-btn v-btn-outline" onClick={onCancel} data-testid="heavy-model-cancel">
            Stay with {defaultModel.label}
          </button>
          <button className="v-btn" onClick={onConfirm} data-testid="heavy-model-confirm" style={{ background: "#F59E0B", borderColor: "#F59E0B", color: "#0a0a0a" }}>
            Use {pending.label} anyway →
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, testId }) {
  return (
    <div>
      <div className="overline" style={{ marginBottom: 6 }}>{label}</div>
      <input className="v-input" value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId} />
    </div>
  );
}
